import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Pressable, type PressableProps } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import {
	BylineConnectionIconStack,
	type BylineConnectionIconStackProps,
} from './BylineConnectionIconStack.tsx';

const i18n = defineMessages({
	label: {
		id: 'byline-connections.label',
		defaultMessage: 'Context',
		description: 'Label for the connections byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		font: token('font.body.small'),
		// The byline row is `white-space: nowrap`; the top-layer tooltip renders
		// inside this subtree and inherits it, overflowing its box. CCINT-8744
		whiteSpace: 'normal',
	},
	wrapperLoaded: {
		color: token('color.text.subtle'),
	},
	wrapperPlaceholder: {
		color: token('color.text.disabled'),
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		columnGap: token('space.050'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
	},
	triggerLoaded: {
		cursor: 'pointer',
		color: token('color.text.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	triggerPlaceholder: {
		cursor: 'inherit',
		color: token('color.text.disabled'),
	},
	triggerExpanded: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

type TriggerAppearance = 'loaded' | 'placeholder';

type BylineConnectionsWrapperProps = ComponentPropsWithoutRef<'span'> & {
	appearance: TriggerAppearance;
	children: ReactNode;
};

export const BylineConnectionsWrapper = ({
	appearance,
	children,
	...htmlAttributes
}: BylineConnectionsWrapperProps): React.JSX.Element => (
	<span
		{...htmlAttributes}
		css={[
			styles.wrapper,
			appearance === 'loaded' ? styles.wrapperLoaded : styles.wrapperPlaceholder,
		]}
	>
		{children}
	</span>
);

type SharedTriggerProps = Pick<
	BylineConnectionIconStackProps,
	'additionalIcons' | 'incomingLinks' | 'links' | 'minIcons'
>;

type LoadedButtonProps = Omit<
	PressableProps,
	'aria-expanded' | 'aria-hidden' | 'children' | 'isDisabled' | 'testId' | 'xcss'
> & {
	'aria-expanded': boolean;
};

type LoadedTriggerProps = SharedTriggerProps & {
	appearance: 'loaded';
	buttonProps: LoadedButtonProps;
};

type PlaceholderTriggerProps = SharedTriggerProps & {
	appearance: 'placeholder';
	buttonProps?: never;
};

type BylineConnectionsTriggerProps = LoadedTriggerProps | PlaceholderTriggerProps;

export const BylineConnectionsTrigger = (
	props: BylineConnectionsTriggerProps,
): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { additionalIcons, appearance, incomingLinks, links, minIcons } = props;
	const isPlaceholder = appearance === 'placeholder';
	const content = (
		<>
			<TeamworkGraphIcon
				label=""
				size="small"
				color={isPlaceholder ? token('color.icon.disabled') : token('color.icon.subtle')}
				testId="byline-connections-teamwork-graph-icon"
			/>
			<Text
				as="span"
				size="small"
				color={isPlaceholder ? 'color.text.disabled' : 'color.text.subtle'}
			>
				{formatMessage(i18n.label)}
			</Text>
			<BylineConnectionIconStack
				links={links}
				incomingLinks={incomingLinks}
				additionalIcons={additionalIcons}
				appearance={isPlaceholder ? 'placeholder' : 'default'}
				minIcons={minIcons}
				spacing="none"
			/>
		</>
	);

	if (props.appearance === 'placeholder') {
		return (
			<Pressable
				isDisabled
				aria-hidden="true"
				testId="byline-connections-trigger"
				xcss={cx(styles.trigger, styles.triggerPlaceholder)}
			>
				{content}
			</Pressable>
		);
	}

	return (
		<Pressable
			{...props.buttonProps}
			testId="byline-connections-trigger"
			xcss={cx(
				styles.trigger,
				styles.triggerLoaded,
				props.buttonProps['aria-expanded'] && styles.triggerExpanded,
			)}
		>
			{content}
		</Pressable>
	);
};
